import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  forwardRef,
  inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import SignaturePad from 'signature_pad';

@Component({
    selector: 'app-signature-pad',
    templateUrl: './signature-pad.component.html',
    styleUrls: ['./signature-pad.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SignaturePadComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class SignaturePadComponent implements AfterViewInit, OnDestroy, ControlValueAccessor {
  @ViewChild('canvas', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;

  @Input() height = 220;
  @Input() penColor = '#111827';
  @Input() minWidth = 0.5;
  @Input() maxWidth = 2.5;
  @Input() backgroundColor = 'rgba(255,255,255,0)';

  @Output() changed = new EventEmitter<string | null>();
  /** Emits true when user starts drawing, false when stroke ends */
  @Output() drawing = new EventEmitter<boolean>();

  private pad!: SignaturePad;
  private pendingValue: string | null = null;
  private onChange: (value: string | null) => void = () => { return; };
  private onTouched: () => void = () => { return; };
  private disabled = false;
  private readonly cdr = inject(ChangeDetectorRef);

  ngAfterViewInit(): void {
    this.initPad();
    this.resizeCanvas();
    if (this.pendingValue) this.fromDataUrl(this.pendingValue);
    this.applyDisabledState();
  }

  ngOnDestroy(): void {
    this.pad?.off();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.resizeCanvas();
  }

  clear(): void {
    if (!this.pad) return;
    this.pad.clear();
    this.emitValue(null);
  }

  isEmpty(): boolean {
    return !this.pad || this.pad.isEmpty();
  }

  toDataUrl(type: 'image/png' | 'image/jpeg' = 'image/png', quality?: number): string | null {
    if (!this.pad || this.pad.isEmpty()) return null;
    return this.pad.toDataURL(type, quality);
  }

  fromDataUrl(dataUrl: string): void {
    if (!this.pad) {
      this.pendingValue = dataUrl;
      return;
    }
    this.pad.clear();
    this.pad.fromDataURL(dataUrl);
    this.emitValue(dataUrl);
  }

  writeValue(value: string | null): void {
    this.pendingValue = value;
    if (!this.pad) return;
    if (!value) {
      this.pad.clear();
      return;
    }
    this.fromDataUrl(value);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.applyDisabledState();
  }

  private initPad(): void {
    this.pad = new SignaturePad(this.canvasRef.nativeElement, {
      penColor: this.penColor,
      minWidth: this.minWidth,
      maxWidth: this.maxWidth,
      backgroundColor: this.backgroundColor,
    });

    this.pad.addEventListener('beginStroke', () => {
      this.drawing.emit(true);
      this.cdr.markForCheck();
    });

    this.pad.addEventListener('endStroke', () => {
      this.drawing.emit(false);
      this.onTouched();
      this.emitValue(this.toDataUrl());
      this.cdr.markForCheck();
    });
  }

  private resizeCanvas(): void {
    if (!this.pad) return;

    const canvas = this.canvasRef.nativeElement;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const prevValue = this.toDataUrl();

    const widthCss = canvas.getBoundingClientRect().width || 600;
    canvas.width = Math.floor(widthCss * ratio);
    canvas.height = Math.floor(this.height * ratio);

    const ctx = canvas.getContext('2d');
    if (ctx) ctx.scale(ratio, ratio);

    this.pad.clear();
    if (prevValue) this.pad.fromDataURL(prevValue);
  }

  private applyDisabledState(): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas) return;
    canvas.style.pointerEvents = this.disabled ? 'none' : 'auto';
    canvas.style.opacity = this.disabled ? '0.65' : '1';
  }

  focusCanvas(): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas) return;
    canvas.scrollIntoView({ behavior: 'smooth', block: 'center' });
    canvas.focus();
  }

  private emitValue(value: string | null): void {
    this.pendingValue = value;
    this.onChange(value);
    this.changed.emit(value);
  }
  
}